/**
 * STRUCTURAL folders — the drive's own scaffolding, never content a person made
 * (#888, #889; docs/plans/2026-10-01_APP_LINK_STRUCTURAL_ROOT_888_889.md §4).
 *
 * The drive root, the reserved top-level folders, each member's `Persons/<email>`
 * space and the containers seeded inside it, the per-source-org folders under
 * `Shared/`, and the pre-revamp `_system` containers. None is ever a sensible APP
 * FOLDER for a share link (serving one hands out the whole drive, a whole section,
 * or another org's shares), and the drive draws them with their own icons so a
 * person can tell scaffolding from a folder they made.
 *
 * Judged by POSITION, never by a name alone: the input is the folder's path of
 * names below the drive root (`[]` = the root itself). Reserved names are reserved
 * only at the root, so `Reports/Library` is an ordinary folder. Position + name is
 * reliable because the create/rename/copy guards refuse a reserved name at the
 * root.
 *
 * Names match the CANONICAL spelling exactly (`Library`, `_system`), unlike the
 * create guard's case-insensitive `isReservedRootName`: the system creates these
 * folders with exactly that name, and a lookalike at the root (`shared`, `library`)
 * can only be a person's folder from before the guard existed — banning it would
 * break their links for nothing.
 *
 * The ONE implementation: the server walks a folder's ancestors into this path
 * (`structuralAppRootReason`, @papan/database); the drive passes its breadcrumbs.
 */
import { LEGACY_CONTAINER, LEGACY_ROOT, LEGACY_USERS_SEGMENT, LEGACY_WORKSPACES_SEGMENT, ROOT } from './reserved-folders.js'

/** What a structural folder is — also picks its drive icon. */
export type StructuralFolderKind =
  | 'drive-root'
  | 'library'
  | 'projects'
  | 'pdapps'
  | 'persons'
  | 'shared'
  | 'system'
  /** `Persons/<email>` — one member's space. */
  | 'person-space'
  /** `Shared/<SourceOrg>` — every share from one org. */
  | 'shared-org'
  /** `_system/workspaces`, `_system/users[/<email>[/workspaces]]`. */
  | 'legacy-container'

/** The deepest a structural folder sits below the drive root (`_system/users/<email>/workspaces`). */
export const STRUCTURAL_MAX_DEPTH = 4

const is = (s: string | undefined, name: string): boolean => s === name

function topLevelKind(name: string): StructuralFolderKind | null {
  if (is(name, ROOT.LIBRARY)) return 'library'
  if (is(name, ROOT.PROJECTS)) return 'projects'
  if (is(name, ROOT.MINIAPPS) || is(name, LEGACY_CONTAINER.MINIAPPS)) return 'pdapps'
  if (is(name, ROOT.PERSONS)) return 'persons'
  if (is(name, ROOT.SHARED) || is(name, LEGACY_ROOT.SHARED)) return 'shared'
  if (is(name, ROOT.SYSTEM) || is(name, LEGACY_ROOT.SYSTEM)) return 'system'
  return null
}

/** The kind of a container seeded inside `Persons/<email>` — the per-person twin of a top-level folder. */
function personChildKind(name: string): StructuralFolderKind | null {
  if (is(name, ROOT.PROJECTS)) return 'projects'
  if (is(name, ROOT.MINIAPPS) || is(name, LEGACY_CONTAINER.MINIAPPS)) return 'pdapps'
  if (is(name, ROOT.SYSTEM)) return 'system'
  return null
}

/**
 * The structural kind of the FOLDER at `path` (names below the drive root), or
 * null for an ordinary folder. Only call it for folders.
 */
export function structuralFolderKind(path: readonly string[]): StructuralFolderKind | null {
  const [a, b, c, d] = path
  switch (path.length) {
    case 0: return 'drive-root'
    case 1: return topLevelKind(a!)
    case 2:
      if (is(a, ROOT.PERSONS)) return 'person-space'
      if (is(a, ROOT.SHARED) || is(a, LEGACY_ROOT.SHARED)) return 'shared-org'
      if (is(a, LEGACY_ROOT.SYSTEM) && (is(b, LEGACY_WORKSPACES_SEGMENT) || is(b, LEGACY_USERS_SEGMENT))) return 'legacy-container'
      return null
    case 3:
      if (is(a, ROOT.PERSONS)) return personChildKind(c!)
      if (is(a, LEGACY_ROOT.SYSTEM) && is(b, LEGACY_USERS_SEGMENT)) return 'legacy-container'
      return null
    case 4:
      if (is(a, LEGACY_ROOT.SYSTEM) && is(b, LEGACY_USERS_SEGMENT) && is(d, LEGACY_WORKSPACES_SEGMENT)) return 'legacy-container'
      return null
    default: return null
  }
}

/** Why a share link refuses a folder as its app folder — the wire `reason` of `structural_app_root`. */
export function structuralAppRootMessage(kind: StructuralFolderKind, folderName: string | null): string {
  // Unnamed: the folder is in ANOTHER org (a reshare) — its name is not the caller's to learn.
  if (folderName === null) return "The folder this page is served from is one of the drive's own folders, so an app link can't serve it. Share a folder inside it instead."
  if (kind === 'drive-root') return "An app link can't serve the whole drive. Put the HTML file in its own folder and share it from there."
  if (kind === 'person-space') return `An app link can't serve a whole personal space (“${folderName}”). Put the HTML file in its own folder and share it from there.`
  if (kind === 'shared-org') return `“${folderName}” holds what another organization shared with you, so an app link can't serve it. Put the HTML file in its own folder and share it from there.`
  return `“${folderName}” is one of the drive's own folders, so an app link can't serve it. Put the HTML file in its own folder and share it from there.`
}

/** The wire error code for a refused structural app folder. */
export const STRUCTURAL_APP_ROOT_CODE = 'structural_app_root'

/** The wire error code for sharing one of the drive's own folders as a whole. */
export const STRUCTURAL_FOLDER_SHARE_CODE = 'structural_folder'

/**
 * Which of the drive's own folders can't be shared AS A WHOLE — no share link, no
 * org share of the folder itself (owner ruling 2026-10-02, replacing 2026-10-01's
 * "none of them"): the PDApps container (every app at once) and the whole drive.
 * Library, Projects, Shared, System and a person's space CAN be shared; for them
 * "allow sharing" is off by default instead (`structuralSharingDefaultOff`). What is
 * INSIDE any of them stays shareable as before.
 */
export function structuralFolderBlocksShare(kind: StructuralFolderKind | null): boolean {
  return kind === 'pdapps' || kind === 'drive-root'
}

/**
 * The drive's own folders whose "allow sharing" is OFF by default (owner ruling
 * 2026-10-02): nobody but the owner may share the folder itself until a value is
 * stored ON it. Not inherited — folders inside keep their usual settings. The drive
 * root is where org-wide sharing is set, so it keeps its real value.
 */
export function structuralSharingDefaultOff(kind: StructuralFolderKind | null): boolean {
  return kind !== null && kind !== 'drive-root'
}

/** Why a folder `structuralFolderBlocksShare` refuses can't be shared as a whole. */
export function structuralShareMessage(kind: StructuralFolderKind, folderName: string | null): string {
  // Unnamed: reached THROUGH a pointer or a reshare — the folder may not be one the caller
  // can open, so the refusal doesn't name it.
  if (folderName === null) return "This points to one of the drive's own folders, which can't be shared as a whole. Share a folder inside it instead."
  if (kind === 'drive-root') return "The whole drive can't be shared. Share a folder inside it instead."
  return `“${folderName}” holds every app, so it can't be shared as a whole. Share an app inside it instead.`
}
